import { useMemo } from "react";

// `useCustomerKPIs(workOrders, proposals, { window })` — computes the
// 4 KPI stats consumed by `<CustomerKPIStrip>` on the customer side-sheet:
// total revenue, won $, lost $, hit-rate %.
//
// Formula (per user spec):
//   won  = sum of completed-WO totals + sum of won-proposal values
//   lost = sum of cancelled-WO totals + sum of lost-proposal values
//   totalRevenue = won + lost
//   hitRate = round(won / totalRevenue * 100)  // 0 when total is 0
//
// #243 (iter168 fix preserved): the Proposal model field is `value`,
// not `total` or `amount`. We coalesce in that priority to stay
// resilient to older test data that may have either shape.
//
// Extracted to a hook (Feb 2026, iter183) so `<CustomerKPIStrip>`
// can be made fully self-contained by consumers that want to skip
// the parent-prop drilling.
//
// iter190 — added optional `{ window }` segmentation. Lets the strip
// double as a compact trend widget for AM/PM reviews. Supported:
//   • 'all'  → no filter (default; preserves the legacy contract)
//   • 'mtd'  → first day of the current month → now
//   • 'ytd'  → Jan 1 of the current year → now
//   • '12m'  → trailing 12 months (rolling, from same date last year)
// Anything else falls back to 'all'. The window filters by the entry's
// transition timestamp — completion / cancel / status change — so the
// numbers reflect when the dollars LANDED, not when the doc was first
// created. Resolver: `completed_at` → `completion_date` → `updated_at`
// → `created_at` (most specific first). Dates that can't be parsed are
// dropped from the window'd buckets — they'd have polluted the total
// with stale rows when 'mtd' was the active filter.

const KNOWN_WINDOWS = new Set(["all", "mtd", "ytd", "12m", "fy", "last_fy"]);

// Fiscal year runs May 1 → Apr 30 (same rule as routes/commissions._fy_bounds).
export function fyBounds(offset = 0, now = new Date()) {
  const startYear = (now.getMonth() >= 4 ? now.getFullYear() : now.getFullYear() - 1) + offset;
  return { start: new Date(startYear, 4, 1).getTime(), end: new Date(startYear + 1, 4, 1).getTime(), label: `FY${String(startYear + 1).slice(-2)}` };
}

// Resolve the "this dollar amount landed at" timestamp for a row.
// Falls back to created_at when later milestones are absent. Returns
// `null` when nothing parseable is available so the window filter can
// drop the row instead of pulling it forward to epoch 0.
function _landedAt(row) {
  const raw = row?.completed_at || row?.completion_date || row?.updated_at || row?.created_at;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

function _windowRange(window, now = new Date()) {
  switch (window) {
    case "mtd": return { start: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), end: null };
    case "ytd": return { start: new Date(now.getFullYear(), 0, 1).getTime(), end: null };
    case "12m": { const d = new Date(now); d.setFullYear(d.getFullYear() - 1); return { start: d.getTime(), end: null }; }
    case "fy": return fyBounds(0, now);
    case "last_fy": return fyBounds(-1, now);
    default: return { start: null, end: null }; // 'all'
  }
}

export function useCustomerKPIs(workOrders, proposals, opts = {}) {
  const window = KNOWN_WINDOWS.has(opts.window) ? opts.window : "all";

  return useMemo(() => {
    const { start, end } = _windowRange(window);
    // 'all' → predicate accepts every row; window'd → drops null timestamps too.
    const inWindow = start === null
      ? () => true
      : (row) => {
          const t = _landedAt(row);
          return t !== null && t >= start && (end === null || t < end);
        };

    const completed = (workOrders || []).filter((wo) => wo.status === "completed" && inWindow(wo));
    const cancelled = (workOrders || []).filter((wo) => wo.status === "cancelled" && inWindow(wo));
    const wonProps = (proposals || []).filter((p) => p.status === "won" && inWindow(p));
    const lostProps = (proposals || []).filter((p) => p.status === "lost" && inWindow(p));

    const completedTotal = completed.reduce((sum, wo) => sum + (wo.total || 0), 0);
    const cancelledTotal = cancelled.reduce((sum, wo) => sum + (wo.total || 0), 0);
    const wonProposalTotal = wonProps.reduce(
      (sum, p) => sum + (p.value || p.amount || p.total || 0),
      0,
    );
    const lostProposalTotal = lostProps.reduce(
      (sum, p) => sum + (p.value || p.amount || p.total || 0),
      0,
    );

    const won = completedTotal + wonProposalTotal;
    const lost = cancelledTotal + lostProposalTotal;
    const totalRevenue = won + lost;
    const hitRate = totalRevenue > 0 ? Math.round((won / totalRevenue) * 100) : 0;
    const wonCount = completed.length + wonProps.length;
    const bidCount = wonCount + cancelled.length + lostProps.length;
    const hitRateCount = bidCount > 0 ? Math.round((wonCount / bidCount) * 100) : 0;

    return { totalRevenue, won, lost, hitRate, wonCount, bidCount, hitRateCount, window };
  }, [workOrders, proposals, window]);
}
